export default `#ifndef PROJECTION_CONSTANTS
#define PROJECTION_CONSTANTS

#define PI 3.141592653589793
#define DEG_TO_RAD 0.017453292519943295
#define RAD_TO_DEG 57.29577951308232
#define MERCATOR_LAT_LIMIT 85.0
#define CYLINDRICAL_EQUAL_AREA_SCALE 1.2792006328649603
#define AZIMUTHAL_CLIP_ANGLE_RAD 3.0194196059501588

#define PROJ_GLOBE 0
#define PROJ_EQUIRECTANGULAR 1
#define PROJ_MERCATOR 2
#define PROJ_ROBINSON 3
#define PROJ_MOLLWEIDE 4
#define PROJ_CYLINDRICAL_EQUAL_AREA 5
#define PROJ_AZIMUTHAL_EQUIDISTANT 6
#define PROJ_AZIMUTHAL_HYBRID 7
#define AZIMUTHAL_RIM_BLEND_START 0.42
#define AZIMUTHAL_RIM_BLEND_END 0.98
#define AZIMUTHAL_FAR_BLEND_START 0.68
#define AZIMUTHAL_FAR_BLEND_END 0.98
#define AZIMUTHAL_HYBRID_BLEND 0.43
#define AZIMUTHAL_HYBRID_RIM_BLEND -0.08
#define AZIMUTHAL_HYBRID_FAR_BLEND -0.14

#endif

const float robinsonK[40] = float[40](
  
  0.9986, -0.09879178,    
  1.0000,  0.00000000,    
  0.9986,  0.09879178,    
  0.9954,  0.19758356,    
  0.9900,  0.29637534,    
  0.9822,  0.39516712,    
  0.9730,  0.49395890,    
  0.9600,  0.59275068,    
  0.9427,  0.69154245,    
  0.9216,  0.79001555,    
  0.8962,  0.88769194,    
  0.8679,  0.98409359,    
  0.8350,  1.07858315,    
  0.7986,  1.17052324,    
  0.7597,  1.25927650,    
  0.7186,  1.34404622,    
  0.6732,  1.42387635,    
  0.6213,  1.49685480,    
  0.5722,  1.55533316,    
  0.5322,  1.59341579     
);

vec3 robinsonYInvert(float absY) {
  if (absY > robinsonK[39]) return vec3(-1.0, 0.0, 0.0);

  int i0 = 0;
  for (int i = 0; i <= 17; i++) {
    if (absY <= robinsonK[(i + 2) * 2 + 1]) {
      i0 = i;
      break;
    }
    i0 = i;
  }

  float ax = robinsonK[i0 * 2];
  float ay = robinsonK[i0 * 2 + 1];
  float bx = robinsonK[(i0 + 1) * 2];
  float by = robinsonK[(i0 + 1) * 2 + 1];
  float cx = robinsonK[(i0 + 2) * 2];
  float cy = robinsonK[(i0 + 2) * 2 + 1];

  float di = (abs(cy - by) > 0.0001) ? (absY - by) / (cy - by) : 0.0;
  di = clamp(di, 0.0, 1.0);
  for (int iter = 0; iter < 5; iter++) {
    float yCoeff = by + di * (cy - ay) / 2.0 + di * di * (cy - 2.0 * by + ay) / 2.0;
    float dyCoeff = (cy - ay) / 2.0 + di * (cy - 2.0 * by + ay);
    if (abs(dyCoeff) < 0.0001) break;
    di = clamp(di - (yCoeff - absY) / dyCoeff, 0.0, 1.0);
  }

  float xCoeff = bx + di * (cx - ax) / 2.0 + di * di * (cx - 2.0 * bx + ax) / 2.0;
  return vec3(float(i0), di, xCoeff);
}

float robinsonXLimit(float y) {
  vec3 inv = robinsonYInvert(abs(y));
  if (inv.x < 0.0) return -1.0;
  return inv.z * PI;
}

bool isInsideProjectionDomain(vec2 projected, int projectionType, float radius) {
  float safeRadius = max(abs(radius), 0.0001);
  vec2 p = projected / safeRadius;
  float epsilon = 0.0005;

  if (projectionType == PROJ_EQUIRECTANGULAR) {
    return abs(p.x) <= PI + epsilon && abs(p.y) <= PI * 0.5 + epsilon;
  } else if (projectionType == PROJ_MERCATOR) {
    float latLimitRad = MERCATOR_LAT_LIMIT * DEG_TO_RAD;
    float yLimit = log(tan(PI / 4.0 + latLimitRad / 2.0));
    return abs(p.x) <= PI + epsilon && abs(p.y) <= yLimit + epsilon;
  } else if (projectionType == PROJ_ROBINSON) {
    float xLimit = robinsonXLimit(p.y);
    return xLimit >= 0.0 && abs(p.x) <= xLimit + epsilon;
  } else if (projectionType == PROJ_MOLLWEIDE) {
    float yLimit = sqrt(2.0);
    float xLimit = 2.0 * sqrt(2.0) * sqrt(max(0.0, 1.0 - (p.y * p.y) / 2.0));
    return abs(p.y) <= yLimit + epsilon && abs(p.x) <= xLimit + epsilon;
  } else if (projectionType == PROJ_CYLINDRICAL_EQUAL_AREA) {
    return
      abs(p.x) <= PI / CYLINDRICAL_EQUAL_AREA_SCALE + epsilon &&
      abs(p.y) <= CYLINDRICAL_EQUAL_AREA_SCALE + epsilon;
  } else if (projectionType == PROJ_AZIMUTHAL_EQUIDISTANT || projectionType == PROJ_AZIMUTHAL_HYBRID) {
    return length(p) <= AZIMUTHAL_CLIP_ANGLE_RAD + epsilon;
  }

  return true;
}

vec2 rotateCoords(float lat, float lon, float centerLon, float centerLat) {
  
  float latRad = lat * DEG_TO_RAD;
  float lonRad = (lon - centerLon) * DEG_TO_RAD;
  float centerLatRad = centerLat * DEG_TO_RAD;

  
  float cosLat = cos(latRad);
  float sinLat = sin(latRad);
  float cosLon = cos(lonRad);
  float sinLon = sin(lonRad);
  float cosCenterLat = cos(centerLatRad);
  float sinCenterLat = sin(centerLatRad);

  
  float newSinLat = sinLat * cosCenterLat - cosLat * cosLon * sinCenterLat;
  float newLat = asin(clamp(newSinLat, -1.0, 1.0));

  
  float y = cosLat * sinLon;
  float x = sinLat * sinCenterLat + cosLat * cosLon * cosCenterLat;
  float newLon = atan(y, x);

  return vec2(newLat * RAD_TO_DEG, newLon * RAD_TO_DEG);
}

float getAzimuthalEffectiveBlend(
  float blend,
  float rimBlend,
  float farBlend,
  float c
) {
  float rimWeight = smoothstep(
    AZIMUTHAL_RIM_BLEND_START,
    AZIMUTHAL_RIM_BLEND_END,
    c / PI
  );
  float farWeight = smoothstep(
    AZIMUTHAL_FAR_BLEND_START,
    AZIMUTHAL_FAR_BLEND_END,
    c / PI
  );
  return clamp(blend + rimBlend * rimWeight + farBlend * farWeight, 0.0, 1.0);
}

vec3 projectGlobe(float lat, float lon, float radius) {
  float latRad = lat * DEG_TO_RAD;
  float lonRad = lon * DEG_TO_RAD;

  float x = radius * cos(latRad) * cos(lonRad);
  float y = radius * cos(latRad) * sin(lonRad);
  float z = radius * sin(latRad);

  return vec3(x, y, z);
}

vec3 projectEquirectangularRotated(vec2 rotated) {
  float x = rotated.y * DEG_TO_RAD;  
  float y = rotated.x * DEG_TO_RAD;  
  return vec3(x, y, 0.0);
}

vec3 projectMercatorRotated(vec2 rotated) {
  float safeLat = clamp(rotated.x, -MERCATOR_LAT_LIMIT, MERCATOR_LAT_LIMIT);
  float latRad = safeLat * DEG_TO_RAD;
  float x = rotated.y * DEG_TO_RAD;
  float y = log(tan(PI / 4.0 + latRad / 2.0));
  return vec3(x, y, 0.0);
}

vec3 projectRobinsonRotated(vec2 rotated) {
  float latRad = rotated.x * DEG_TO_RAD;

  
  float i = min(18.0, abs(latRad) * 36.0 / PI);
  int i0 = int(floor(i));
  float di = i - float(i0);

  
  
  float ax = robinsonK[i0 * 2];
  float ay = robinsonK[i0 * 2 + 1];
  float bx = robinsonK[(i0 + 1) * 2];
  float by = robinsonK[(i0 + 1) * 2 + 1];
  int i2 = min(i0 + 2, 19);
  float cx = robinsonK[i2 * 2];
  float cy = robinsonK[i2 * 2 + 1];

  
  
  float xCoeff = bx + di * (cx - ax) / 2.0 + di * di * (cx - 2.0 * bx + ax) / 2.0;
  float yCoeff = by + di * (cy - ay) / 2.0 + di * di * (cy - 2.0 * by + ay) / 2.0;

  
  
  float x = xCoeff * rotated.y * DEG_TO_RAD;
  float y = sign(rotated.x) * yCoeff;

  return vec3(x, y, 0.0);
}

vec3 projectMollweideRotated(vec2 rotated) {
  float latRad = rotated.x * DEG_TO_RAD;

  
  float theta = latRad;
  float target = PI * sin(latRad);

  
  for (int iter = 0; iter < 5; iter++) {
    float f = 2.0 * theta + sin(2.0 * theta) - target;
    float df = 2.0 + 2.0 * cos(2.0 * theta);
    if (abs(df) < 0.0001) break;
    theta = theta - f / df;
  }

  float x = (2.0 * sqrt(2.0) / PI) * rotated.y * DEG_TO_RAD * cos(theta);
  float y = sqrt(2.0) * sin(theta);

  return vec3(x, y, 0.0);
}

vec3 projectCylindricalEqualAreaRotated(vec2 rotated) {
  float x = rotated.y * DEG_TO_RAD / CYLINDRICAL_EQUAL_AREA_SCALE;
  float y = sin(rotated.x * DEG_TO_RAD) * CYLINDRICAL_EQUAL_AREA_SCALE;
  return vec3(x, y, 0.0);
}

vec3 projectAzimuthalHybridRotated(vec2 rotated) {
  float latRad = rotated.x * DEG_TO_RAD;
  float lonRad = rotated.y * DEG_TO_RAD;

  float cosLat = cos(latRad);
  float sinLat = sin(latRad);
  float cosLon = cos(lonRad);
  float sinLon = sin(lonRad);
  float cosC = clamp(cosLat * cosLon, -1.0, 1.0);
  float c = acos(cosC);

  if (c > AZIMUTHAL_CLIP_ANGLE_RAD) {
    float nan = sqrt(-1.0);
    return vec3(nan, nan, nan);
  }
  if (c < 0.0001) {
    return vec3(0.0, 0.0, 0.0);
  }

  float sinC = sin(c);
  if (abs(sinC) < 0.0001) {
    float nan = sqrt(-1.0);
    return vec3(nan, nan, nan);
  }

  float rhoEqualArea = sqrt(max(0.0, 2.0 - 2.0 * cosC));
  float rhoEquidistant = c;
  float effectiveBlend = getAzimuthalEffectiveBlend(
    AZIMUTHAL_HYBRID_BLEND,
    AZIMUTHAL_HYBRID_RIM_BLEND,
    AZIMUTHAL_HYBRID_FAR_BLEND,
    c
  );
  float rho = mix(rhoEqualArea, rhoEquidistant, effectiveBlend);
  float k = rho / sinC;

  return vec3(k * cosLat * sinLon, k * sinLat, 0.0);
}

vec3 projectAzimuthalEquidistantRotated(vec2 rotated) {
  float latRad = rotated.x * DEG_TO_RAD;
  float lonRad = rotated.y * DEG_TO_RAD;

  float cosC = cos(latRad) * cos(lonRad);
  float c = acos(clamp(cosC, -1.0, 1.0));

  if (c > AZIMUTHAL_CLIP_ANGLE_RAD) {
    float nan = sqrt(-1.0);
    return vec3(nan, nan, nan);
  }
  if (c < 0.0001) {
    return vec3(0.0, 0.0, 0.0);
  }

  float sinC = sin(c);
  float k = c / sinC;

  float x = k * cos(latRad) * sin(lonRad);
  float y = k * sin(latRad);

  return vec3(x, y, 0.0);
}

vec3 projectRotatedLatLon(vec2 rotated, int projectionType, float radius) {
  if (projectionType == PROJ_EQUIRECTANGULAR) {
    return projectEquirectangularRotated(rotated) * radius;
  } else if (projectionType == PROJ_MERCATOR) {
    return projectMercatorRotated(rotated) * radius;
  } else if (projectionType == PROJ_ROBINSON) {
    return projectRobinsonRotated(rotated) * radius;
  } else if (projectionType == PROJ_MOLLWEIDE) {
    return projectMollweideRotated(rotated) * radius;
  } else if (projectionType == PROJ_CYLINDRICAL_EQUAL_AREA) {
    return projectCylindricalEqualAreaRotated(rotated) * radius;
  } else if (projectionType == PROJ_AZIMUTHAL_HYBRID) {
    return projectAzimuthalHybridRotated(rotated) * radius;
  } else if (projectionType == PROJ_AZIMUTHAL_EQUIDISTANT) {
    return projectAzimuthalEquidistantRotated(rotated) * radius;
  } else {
    return projectGlobe(rotated.x, rotated.y, radius);
  }
}

vec3 projectLatLon(
  float lat,
  float lon,
  int projectionType,
  float centerLon,
  float centerLat,
  float radius
) {
  if (projectionType == PROJ_GLOBE) {
    return projectGlobe(lat, lon, radius);
  }

  vec2 rotated = rotateCoords(lat, lon, centerLon, centerLat);
  return projectRotatedLatLon(rotated, projectionType, radius);
}
bool is_nan(float val) {
    uint bits = floatBitsToUint(val);
    
    
    return (bits & 0x7F800000u) == 0x7F800000u && (bits & 0x007FFFFFu) != 0u;
}

uniform int projectionType;
uniform float centerLon;
uniform float centerLat;
uniform float projectionRadius;
uniform float zOffset;

attribute vec2 latLon;
attribute vec2 segmentOtherLatLon;

varying float vHidden;
varying vec2 vProjectedXY;

bool isInvalidProjection(vec3 projected) {
  return is_nan(projected.x) || is_nan(projected.y) || is_nan(projected.z);
}

void main() {
  vec3 projected = projectLatLon(
    latLon.x,
    latLon.y,
    projectionType,
    centerLon,
    centerLat,
    projectionRadius
  );
  vec2 rotated = rotateCoords(
    latLon.x,
    latLon.y,
    centerLon,
    centerLat
  );
  vec2 otherRotated = rotateCoords(
    segmentOtherLatLon.x,
    segmentOtherLatLon.y,
    centerLon,
    centerLat
  );
  vec3 otherProjected = projectLatLon(
    segmentOtherLatLon.x,
    segmentOtherLatLon.y,
    projectionType,
    centerLon,
    centerLat,
    projectionRadius
  );

  bool hideSegment =
    isInvalidProjection(projected) ||
    isInvalidProjection(otherProjected) ||
    (projectionType != PROJ_GLOBE &&
      abs(rotated.y - otherRotated.y) > 180.0);

  
  
  
  
  if (!hideSegment && (projectionType == PROJ_AZIMUTHAL_EQUIDISTANT || projectionType == PROJ_AZIMUTHAL_HYBRID)) {
    hideSegment = distance(projected.xy, otherProjected.xy) > projectionRadius;
  }

  vHidden = hideSegment ? 1.0 : 0.0;
  if (hideSegment) {
    vProjectedXY = vec2(0.0);
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }

  vProjectedXY = projected.xy;
  projected.z += zOffset;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(projected, 1.0);
}`